import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Skeleton,
  Alert,
  Button,
  Stack,
  useTheme,
  alpha,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ArrowBack, SportsBaseball, ChevronLeft, ChevronRight, CalendarToday } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import Boxscore from '../components/Boxscore';
import { gamesApi } from '../services/apiService';
import { getTeamLogoUrl } from '../utils/teamLogos';

// Format date for display
const formatDate = (d) => {
  if (!d) return '';
  try {
    const dt = new Date(d + 'T12:00:00');
    return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return d; }
};

// Get adjacent date
const getAdjacentDate = (dateStr, offset) => {
  try {
    const dt = new Date(dateStr + 'T12:00:00');
    dt.setDate(dt.getDate() + offset);
    return dt.toISOString().split('T')[0];
  } catch { return null; }
};

const BoxscorePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { gameId, date } = useParams();
  const [game, setGame] = useState(null);
  const [allGames, setAllGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGame = async (isInitial = true) => {
      try {
        if (isInitial) {
          setLoading(true);
          setError(null);
        }
        let games = [];

        // Try to get the game from live MLB data first
        if (date) {
          try {
            const data = await gamesApi.getLiveSchedule(date);
            games = data.games || [];
            const foundGame = games.find(g => g.id?.toString() === gameId);
            if (foundGame) {
              setGame(foundGame);
              setAllGames(games);
              if (isInitial) setLoading(false);
              return foundGame;
            }
          } catch (liveErr) {
            if (isInitial) console.warn('Live schedule fetch failed, trying cache:', liveErr.message);
          }
        }

        // Fallback to Redis cache (only on initial load — live polling only uses live API)
        if (isInitial) {
          const response = await gamesApi.getRecentGames({ date, limit: 20 });
          games = response.games || [];
          const foundGame = games.find(g => g.id?.toString() === gameId);

          if (foundGame) {
            setGame(foundGame);
            setAllGames(games);
          } else {
            setError('Game not found');
          }
        }
        return null;
      } catch (err) {
        if (isInitial) {
          console.error('Failed to fetch game:', err);
          setError('Failed to load game data');
        }
      } finally {
        if (isInitial) setLoading(false);
      }
    };

    if (!gameId) return;
    let cancelled = false;
    let interval;

    (async () => {
      const initial = await fetchGame(true);
      if (cancelled) return;

      // If the game is live (or schedule says it could become live), poll every 20s.
      const status = (initial?.status || '').toLowerCase();
      const startedLive = initial?.isLive
        || status.includes('in progress')
        || status.includes('live')
        || status.includes('warmup')
        || status.includes('pre-game')
        || status.includes('manager challenge');
      const startedFinal = initial?.isFinal || status.includes('final') || status.includes('completed');

      if (startedLive || (!startedFinal && initial)) {
        interval = setInterval(() => {
          if (!cancelled) fetchGame(false);
        }, 20000);
      }
    })();

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
  }, [gameId, date]);

  // Find current game index for prev/next navigation
  const currentIdx = allGames.findIndex(g => g.id?.toString() === gameId);
  const prevGame = currentIdx > 0 ? allGames[currentIdx - 1] : null;
  const nextGame = currentIdx < allGames.length - 1 ? allGames[currentIdx + 1] : null;

  // Extract team info from game data
  const awayTeam = game?.awayTeam?.abbreviation || game?.teams?.away?.team?.abbreviation || game?.away || '';
  const homeTeam = game?.homeTeam?.abbreviation || game?.teams?.home?.team?.abbreviation || game?.home || '';
  const awayScore = game?.awayScore ?? game?.teams?.away?.score ?? '';
  const homeScore = game?.homeScore ?? game?.teams?.home?.score ?? '';
  const status = game?.status || game?.gameStatus || '';
  const isFinal = typeof status === 'string' && status.toLowerCase().includes('final');
  const isLive = typeof status === 'string' && (status.toLowerCase().includes('in progress') || status.toLowerCase().includes('live'));

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Stack spacing={3}>
          {/* Game header skeleton */}
          <Card sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Skeleton variant="circular" width={64} height={64} sx={{ mx: 'auto', mb: 1 }} />
                <Skeleton width={60} height={24} sx={{ mx: 'auto' }} />
              </Box>
              <Skeleton width={80} height={48} />
              <Box sx={{ textAlign: 'center' }}>
                <Skeleton variant="circular" width={64} height={64} sx={{ mx: 'auto', mb: 1 }} />
                <Skeleton width={60} height={24} sx={{ mx: 'auto' }} />
              </Box>
            </Box>
          </Card>
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
        </Stack>
      </Container>
    );
  }

  if (error || !game) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Stack spacing={3}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" sx={{ alignSelf: 'flex-start' }}>
            Back
          </Button>
          {error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <SportsBaseball sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h5" color="text.secondary" gutterBottom>Game Not Found</Typography>
              <Typography variant="body1" color="text.secondary">The requested game could not be found.</Typography>
            </Box>
          )}
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack spacing={3}>
        {/* Navigation bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} size="small" sx={{ fontWeight: 600 }}>
            Back
          </Button>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {prevGame && (
              <Tooltip title={`${prevGame.awayTeam?.abbreviation || prevGame.away || ''} @ ${prevGame.homeTeam?.abbreviation || prevGame.home || ''}`}>
                <IconButton size="small" onClick={() => navigate(`/boxscore/${prevGame.id}/${date}`)}>
                  <ChevronLeft />
                </IconButton>
              </Tooltip>
            )}
            <Chip
              icon={<CalendarToday sx={{ fontSize: 14 }} />}
              label={formatDate(date)}
              size="small"
              sx={{ fontWeight: 600, fontSize: '0.8rem' }}
            />
            {nextGame && (
              <Tooltip title={`${nextGame.awayTeam?.abbreviation || nextGame.away || ''} @ ${nextGame.homeTeam?.abbreviation || nextGame.home || ''}`}>
                <IconButton size="small" onClick={() => navigate(`/boxscore/${nextGame.id}/${date}`)}>
                  <ChevronRight />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* ── Game Header Card ── */}
        <Card
          elevation={0}
          sx={{
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)}, ${alpha(theme.palette.background.paper, 1)})`,
          }}
        >
          <Box sx={{
            p: { xs: 2, sm: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 2, sm: 5 },
            flexWrap: 'wrap',
          }}>
            {/* Away Team */}
            <Box sx={{ textAlign: 'center', minWidth: 80 }}>
              <Box
                component="img"
                src={getTeamLogoUrl(awayTeam)}
                alt={awayTeam}
                sx={{ width: { xs: 48, sm: 64 }, height: { xs: 48, sm: 64 }, mx: 'auto', mb: 0.5 }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
                {awayTeam}
              </Typography>
            </Box>

            {/* Score */}
            <Box sx={{ textAlign: 'center', minWidth: 100 }}>
              {(awayScore !== '' && homeScore !== '') ? (
                <>
                  <Typography
                    variant="h3"
                    fontWeight={900}
                    sx={{
                      fontSize: { xs: '2rem', sm: '2.8rem' },
                      letterSpacing: '0.05em',
                      lineHeight: 1,
                    }}
                  >
                    {awayScore} – {homeScore}
                  </Typography>
                  <Chip
                    label={isFinal ? 'Final' : isLive ? 'Live' : status || 'Scheduled'}
                    size="small"
                    sx={{
                      mt: 1,
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      bgcolor: isLive ? alpha('#4caf50', 0.15) : isFinal ? alpha(theme.palette.text.secondary, 0.08) : alpha(theme.palette.info.main, 0.1),
                      color: isLive ? '#4caf50' : isFinal ? 'text.secondary' : theme.palette.info.main,
                      ...(isLive && {
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.6 } },
                      }),
                    }}
                  />
                </>
              ) : (
                <Typography variant="h5" fontWeight={700} color="text.secondary">
                  @
                </Typography>
              )}
            </Box>

            {/* Home Team */}
            <Box sx={{ textAlign: 'center', minWidth: 80 }}>
              <Box
                component="img"
                src={getTeamLogoUrl(homeTeam)}
                alt={homeTeam}
                sx={{ width: { xs: 48, sm: 64 }, height: { xs: 48, sm: 64 }, mx: 'auto', mb: 0.5 }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
                {homeTeam}
              </Typography>
            </Box>
          </Box>
        </Card>

        {/* Boxscore Content */}
        <Box>
          <Boxscore
            key={game?.id}
            game={game}
            open={true}
            onClose={() => {}}
            embedded={true}
          />
        </Box>
      </Stack>
    </Container>
  );
};

export default BoxscorePage;
